/**
 * Home API — List home cards for the school admin dashboard.
 *
 * GET /v1/home/admin
 *
 * Returns every home card (ContentRecord with presentationStyle = HOME_CARD)
 * for the active school, regardless of audience match, dismissal state, or
 * publish/expiry window. Surfaces status fields so the admin UI can label
 * cards as scheduled / live / expired / retracted.
 */

import { z } from 'zod'

export const HomeMessageAdminStatusSchema = z.enum(['DRAFT', 'SCHEDULED', 'PUBLISHED', 'EXPIRED', 'ARCHIVED'])

export type HomeMessageAdminStatus = z.infer<typeof HomeMessageAdminStatusSchema>

/**
 * One media row attached to a home card, cover first.
 *
 * `id` is the Media Library `publicId` — the identifier
 * `PUT /v1/content/:id` reconciles `ContentRecordMedia` against
 * (`data.attachments[].mediaId`). It is the empty string for a legacy card
 * whose images live only in `attachmentsJson` and were never linked as media
 * rows; an id-less entry is round-tripped by URL and never named as a removal.
 *
 * `url` is nullable on purpose. EVERY join row is returned, including one whose
 * `Media` has no `cloudinaryUrl` yet (still processing, or a failed derive).
 * Hiding such a row would make the composer's set incomplete, and the composer
 * asserts completeness — a title-only save would then delete the row it never
 * saw. The composer renders a placeholder for a null url instead.
 *
 * `isCover` is true ONLY for the row the record's `coverMediaId` names. It is
 * never fabricated onto the leading row: the reader orders the resolved cover
 * first, and "which row leads" is a display decision, not a fact about the
 * record.
 */
export const HomeMessageAdminMediaSchema = z.object({
  id: z.string(),
  url: z.string().nullable(),
  isCover: z.boolean()
})

export type HomeMessageAdminMedia = z.infer<typeof HomeMessageAdminMediaSchema>

export const HomeMessageAdminViewSchema = z.object({
  publicId: z.string(),
  title: z.string().nullable(),
  body: z.string().nullable(),
  /**
   * Card kind. Defaults to `message` so pre-`cardKind` rows render as classic
   * message cards in the admin list. A `link` card carries a `linkUrl`.
   */
  cardKind: z.enum(['message', 'link']).default('message'),
  /** Target URL for a `link` card; null for a message card. */
  linkUrl: z.string().nullable().default(null),
  /**
   * The card's media set, cover first — the shape the composer needs to edit
   * it. Read `media[0]?.url` for the card thumbnail.
   *
   * Optional and WITHOUT a default on purpose. The composer submits its media
   * set as authoritative (`attachmentsIncludeAllMedia`), which is only sound if
   * it actually saw every row. A default of `[]` would make an old response
   * from a backend that predates this field indistinguishable from a card with
   * no media — the composer would then claim completeness over a set it never
   * received and the next save would delete every existing row. Absent stays
   * absent, and the client tests `Array.isArray(media)` before claiming
   * anything.
   */
  media: z.array(HomeMessageAdminMediaSchema).optional(),
  /**
   * @deprecated Read `media` instead — `media[0]` carries the id and the cover
   * flag this array cannot, and a url-less row is invisible here.
   *
   * A flattened list of the non-null `media` urls, kept for ONE release so a
   * client deployed against the previous shape does not blank its thumbnails
   * during a rollout. Remove once no caller reads it.
   */
  images: z.array(z.string()).optional(),
  /**
   * Resolved target groups. Empty array means "everyone" — but ONLY when
   * `hasUnrepresentableTargets` is false; see that field.
   */
  targets: z.array(
    z.object({
      id: z.string(),
      name: z.string()
    })
  ),
  /**
   * True when the record stores a target this view could not render as a group
   * chip — a section, a list, or any other cut.
   *
   * `targets` above carries GROUPS only, so without this flag a card cut to a
   * section is indistinguishable from a card addressed to everyone: both arrive
   * as `[]`. The composer read `[]` as "no groups picked", the card adapter
   * rewrote that to the school-wide `app_message` target, and a body-only edit
   * silently widened the card's audience to the whole school.
   *
   * ONLY a sole, tag-less `app_message` entry is representable — it is exactly
   * what an empty group set means on the wire, so it round-trips faithfully.
   * An `app_message` carrying `audience_tags`, or any mix of it with other
   * targets, is a real cut this view cannot express. Anything else sets this
   * flag, and the composer then leaves the audience alone instead of flattening
   * it to the groups it happens to understand.
   *
   * OPTIONAL, and the client must treat ABSENT as "cannot account for the
   * audience" (`hasUnrepresentableTargets !== false`). The web CASTS this
   * response, it never Zod-parses it, so a required field does not fail loud —
   * it fails OPEN: a Vercel deploy ahead of ECS, or a cached payload, yields
   * `undefined`, a falsy check passes, and the widening path reopens. Same
   * reasoning as `media` above: absent is not the same as false.
   */
  hasUnrepresentableTargets: z.boolean().optional(),
  pinned: z.boolean(),
  dismissible: z.boolean(),
  /** Person who created the post (from UsersOnSchools). Null when unattributed. */
  author: z
    .object({
      name: z.string(),
      photoUrl: z.string().nullable()
    })
    .nullable(),
  notificationMode: z.enum(['NONE', 'NORMAL', 'ALL', 'EMERGENCY']),
  publishedAt: z.string().datetime().nullable(),
  scheduledAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime().nullable(),
  status: HomeMessageAdminStatusSchema,
  /**
   * The record's OWN `ContentRecord.status`, unmassaged.
   *
   * `status` above is derived for display: a PUBLISHED record with a future
   * `publishedAt` is reported as SCHEDULED, which is right for a badge and
   * wrong for a decision. "Publish now" writes READY_TO_PUBLISH, and doing that
   * to an already-published record fails with CONTENT_ALREADY_PUBLISHED and
   * strands it, so the action is offered on the RAW status only.
   */
  rawStatus: z.string(),
  /**
   * An opaque fingerprint of this card's MEDIA SET — the freshness token behind
   * `attachmentsIncludeAllMedia`.
   *
   * The composer claims to enumerate every media row, and that claim describes
   * the record AS IT WAS WHEN READ. A join row added afterwards (a second admin,
   * or a reopen before this response's refetch lands) is absent from the
   * submitted set only because the client never saw it — and the authoritative
   * reconcile would delete it as a deliberate removal.
   *
   * The client echoes this back as `attachmentsVersion`; the backend recomputes
   * it and refuses the claim on any mismatch, falling back to the additive path.
   *
   * Deliberately NOT the record's `updatedAt`: that moves on reorder and on the
   * scheduled-publish sweep, lags on the read replica right after the author's
   * own save, and is NULL on older rows. This changes when the media set
   * changes, and not otherwise. See `services/content/shared/media-set-version.ts`.
   *
   * OPTIONAL, like `media`: a response that predates this field must not be
   * mistaken for one asserting an empty set. Absent means the client cannot
   * claim, and it withholds the claim rather than sending one that would be
   * refused. Compare for equality only — never parse it.
   */
  mediaSetVersion: z.string().optional()
})

export type HomeMessageAdminView = z.infer<typeof HomeMessageAdminViewSchema>

export const ListHomeMessagesForAdminResponseSchema = z.object({
  cards: z.array(HomeMessageAdminViewSchema)
})

export type ListHomeMessagesForAdminResponse = z.infer<typeof ListHomeMessagesForAdminResponseSchema>
